import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { UploadCloud, AlertTriangle } from "lucide-react";

export default function Upload() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [caption, setCaption] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [limits, setLimits] = useState(null);

    useEffect(() => {
        if (!authLoading && !user) navigate("/login");
    }, [user, authLoading, navigate]);

    useEffect(() => {
        if (user) api.get("/me/limits").then((r) => setLimits(r.data)).catch(() => {});
    }, [user]);

    const pick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Please pick an image file");
            return;
        }
        if (f.size > 10 * 1024 * 1024) {
            toast.error("Max 10MB");
            return;
        }
        setFile(f);
        setPreview(URL.createObjectURL(f));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!file) { toast.error("Add an image first"); return; }
        if (!agreed) { toast.error("You must agree to house rules"); return; }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("caption", caption);
            const { data } = await api.post("/posts", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Posted!");
            navigate(`/p/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main className="max-w-2xl mx-auto px-4 py-8">
            <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                <h1 className="display text-3xl sm:text-4xl font-black">New post</h1>
                {limits && (
                    <div className="inline-flex items-center gap-2 brut-inset bg-white rounded-full px-3 py-1.5" data-testid="limits-hint">
                        <div className={`text-xs font-black ${limits.posts_remaining === 0 ? "text-[#FF5A5F]" : ""}`}>
                            {limits.posts_today}/{limits.daily_post_limit === 9999 ? "∞" : limits.daily_post_limit} today
                        </div>
                        {limits.tier === "free" && limits.posts_remaining <= 2 && (
                            <Link to="/pricing" className="text-[10px] font-black uppercase tracking-wider text-[#0A0A0A] underline hover:text-[#FF5A5F]" data-testid="limits-upgrade">
                                {limits.posts_remaining === 0 ? "Upgrade →" : "Need more?"}
                            </Link>
                        )}
                    </div>
                )}
            </div>

            <div className="bg-[#FFE55C] brut-static rounded-xl p-5 mb-6 flex gap-3" data-testid="house-rules">
                <AlertTriangle className="w-6 h-6 shrink-0" />
                <div className="text-sm font-medium leading-relaxed">
                    <div className="font-black uppercase tracking-wider text-xs mb-1">House rules</div>
                    <ul className="list-disc pl-4 space-y-0.5">
                        <li>Feet only, strictly SFW.</li>
                        <li>Your own photos — no scraping, no minors, ever.</li>
                        <li>Be kind in comments. Rate the pic, not the person.</li>
                    </ul>
                </div>
            </div>

            <form onSubmit={submit} className="space-y-5 bg-white brut-static rounded-xl p-5 sm:p-6" data-testid="upload-form">
                <label
                    htmlFor="file-picker"
                    className="block cursor-pointer border-2 border-dashed border-[#0A0A0A] rounded-xl p-6 text-center hover:bg-[#FFFDF7]"
                    data-testid="file-dropzone"
                >
                    {preview ? (
                        <img src={preview} alt="preview" className="max-h-80 mx-auto rounded-lg border-2 border-[#0A0A0A]" />
                    ) : (
                        <div className="flex flex-col items-center gap-2">
                            <UploadCloud className="w-8 h-8" />
                            <div className="font-bold">Tap to choose an image</div>
                            <div className="text-xs text-[#4A4A4A]">JPG / PNG / WEBP · max 10MB</div>
                        </div>
                    )}
                    <input
                        id="file-picker"
                        type="file"
                        accept="image/*"
                        onChange={pick}
                        className="hidden"
                        data-testid="file-input"
                    />
                </label>

                <div>
                    <label className="text-xs font-bold uppercase tracking-wide">Caption (optional)</label>
                    <textarea
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        maxLength={280}
                        rows={3}
                        placeholder="Beach day, fresh pedi…"
                        className="w-full mt-1 px-4 py-3 rounded-lg brut-inset bg-white font-medium focus:outline-none"
                        data-testid="upload-caption"
                    />
                </div>

                <label className="flex items-start gap-2 text-sm font-medium cursor-pointer" data-testid="agree-rules-label">
                    <input
                        type="checkbox"
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                        className="mt-1 w-4 h-4 border-2 border-[#0A0A0A]"
                        data-testid="agree-rules-checkbox"
                    />
                    <span>I confirm this photo follows the house rules (SFW, my own, adult).</span>
                </label>

                <button
                    type="submit"
                    disabled={submitting || !file || !agreed}
                    className="w-full px-4 py-3 rounded-lg brut bg-[#FFB8D1] font-bold disabled:opacity-50"
                    data-testid="upload-submit"
                >
                    {submitting ? "Posting…" : "Post it"}
                </button>
            </form>
        </main>
    );
}
